// Public build-story page at pkntops.com/build-story
//
// Long-form retrospective of how IMAGE Studios got its software platform —
// 38 days, three repos, three architecture pivots. Mirrors the Cadence
// Advisers build-story pattern (cadenceadvisers.com/build-story) but with a
// longer evolution arc.
//
// Lives outside the auth gate. Same public-route pattern as Privacy + Terms.

// Per-day commit counts across pknt-ops-ios + image-hub-web + image-studios-backend.
// Pulled from `git log --format='%ad' --date=short` on 2026-05-06.
const dailyCommits: { date: string; count: number; label?: string }[] = [
  { date: '2026-04-17', count: 26,  label: 'iOS repo init' },
  { date: '2026-04-18', count: 109, label: 'Peak day' },
  { date: '2026-04-19', count: 50 },
  { date: '2026-04-20', count: 94,  label: 'Backend Migration Plan' },
  { date: '2026-04-21', count: 39 },
  { date: '2026-04-22', count: 12,  label: 'Recovery day' },
  { date: '2026-04-23', count: 21,  label: 'Supabase repo init' },
  { date: '2026-04-24', count: 47,  label: 'Hub repo + national plan' },
  { date: '2026-04-25', count: 43,  label: 'ADR-023 — GHL as dumb pipe' },
  { date: '2026-04-26', count: 16,  label: 'Tranches 1, 1.5, 4 ship' },
  { date: '2026-04-27', count: 7,   label: 'Brookline online' },
  { date: '2026-04-28', count: 25 },
  { date: '2026-04-29', count: 8 },
  { date: '2026-04-30', count: 16 },
  { date: '2026-05-01', count: 9,   label: 'Multi-tenancy ships' },
  { date: '2026-05-04', count: 3 },
  { date: '2026-05-05', count: 7,   label: 'visionOS polish' },
  { date: '2026-05-06', count: 8,   label: 'App Store ready' },
  { date: '2026-05-07', count: 13,  label: 'Readiness phase — security + AI reliability' },
];

const peakCommits = Math.max(...dailyCommits.map((d) => d.count));

export default function BuildStory() {
  return (
    <div className="min-h-screen bg-brand-fog text-brand-deep">
      {/* ─── Hero ─────────────────────────────────────────── */}
      <header className="px-6 pt-20 pb-12 max-w-4xl mx-auto text-center">
        <p className="text-[11px] tracking-[0.25em] uppercase text-brand-slate/70 mb-6">
          A build retrospective · May 2026
        </p>
        <h1 className="font-display text-5xl sm:text-6xl text-brand-deep mb-6 leading-tight tracking-tight">
          How a salon-suite operator
          <br />
          <span className="text-image-pink">rebuilt his platform</span>
          <br />
          in 38 days.
        </h1>
        <p className="text-lg text-brand-slate max-w-2xl mx-auto leading-relaxed">
          March 29 to May 6, 2026. Three repositories. Three architecture pivots. One operator with a
          single goal: make this platform replicate to every IMAGE Studios franchise location with a
          config change, not a copy-paste.
        </p>
      </header>

      {/* ─── Stats grid ───────────────────────────────────── */}
      <section className="px-6 py-12 max-w-5xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-mist">
          <Stat n="38" label="days from first session note to App Store ready" />
          <Stat n="540+" label="production commits across three repos" />
          <Stat n="89,000" label="lines of Swift + TypeScript shipped" />
          <Stat n="129,000" label="total lines of churn on iOS alone" />
          <Stat n="182" label="architecture, decision, and session docs written" />
          <Stat n="19" label="SQL migrations applied to production" />
          <Stat n="3" label="platform pivots in five weeks" />
          <Stat n="6" label="distinct systems running by the end" />
        </div>
      </section>

      {/* ─── Velocity chart ───────────────────────────────── */}
      <section className="px-6 py-12 max-w-5xl mx-auto">
        <h2 className="font-display text-2xl text-brand-deep mb-2">Daily commit velocity</h2>
        <p className="text-sm text-brand-slate mb-8">
          April 17 (iOS repo init) through May 6 (App Store ready). Combined across three
          repositories. April 18 was the peak — 109 commits in a single day. The chart starts when
          iOS opens; the previous three weeks of web platform work shipped on the BASE44 platform
          and aren't reflected here.
        </p>

        <div className="flex items-end gap-[2px] h-44 mb-2">
          {dailyCommits.map((d) => (
            <div
              key={d.date}
              className="flex-1 group relative cursor-default"
              title={`${d.date} — ${d.count} commits${d.label ? ` (${d.label})` : ''}`}
            >
              <div
                className={`w-full rounded-t transition-all ${
                  d.label ? 'bg-image-pink' : 'bg-image-pink/40 group-hover:bg-image-pink/60'
                }`}
                style={{ height: `${(d.count / peakCommits) * 100}%` }}
              />
              {d.label && (
                <div className="absolute -top-12 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-wider text-image-pink whitespace-nowrap font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                  {d.label}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-brand-slate uppercase tracking-wider">
          <span>Apr 17</span>
          <span>· peak: {peakCommits} commits ·</span>
          <span>May 6</span>
        </div>
      </section>

      {/* ─── Phase narrative ──────────────────────────────── */}
      <main className="px-6 py-12 max-w-3xl mx-auto">
        {/* Phase 0 — context only */}
        <Phase
          number="0"
          title="The starting point"
          subtitle="Pre-March 29"
          tag="Context"
        >
          <p>
            A salon-suite operator running the business through five different tools — one for
            financials, one for texting tenants and tracking contacts, one for automated tasks,
            one low-code platform powering the tenant portal, and a folder full of spreadsheets.
            None of them talked to each other. The integration layer was the operator himself,
            copying numbers from one screen to another and holding the entire context of the
            business in his head.
          </p>
          <p>
            The rebuild started with a simple question: what does it look like when all of this
            lives in one place?
          </p>
          <details className="mt-8 group">
            <summary className="cursor-pointer text-sm font-semibold text-brand-slate hover:text-image-pink select-none list-none flex items-center gap-2">
              <span className="inline-block transition-transform group-open:rotate-90">▸</span>
              Engineering details
            </summary>
            <div className="mt-4 pl-4 border-l-2 border-brand-mist space-y-4 text-sm text-brand-slate">
              <p>
                The five surfaces were Buildium (general ledger + rent ledger),
                GoHighLevel (CRM + SMS gateway + sub-account-level workflow engine),
                n8n self-hosted on Fly.io ($20/mo) for cross-system orchestration,
                BASE44 as a low-code app builder for the tenant-facing portal, and
                tabular CSVs maintained in Google Sheets for studio pricing, rent
                paid, and Buildium GL transaction exports. Every surface had its
                own auth model, its own data model, and its own change-management
                story. The integration glue lived in n8n workflows and the
                operator's working memory.
              </p>
            </div>
          </details>
        </Phase>

        {/* Phase 1 — Web platform first */}
        <Phase
          number="1"
          title="The web platform comes first"
          subtitle="March 29 — April 16"
          tag="BASE44 era · 13 pages"
        >
          <p>
            Before iOS existed, before any of the current backend existed, there was a{' '}
            <strong>web platform</strong>. Three weeks of compounding shipping on a low-code
            builder, with public-facing HTML pages on the CRM's own hosting layer. By the time
            anything else started, the operator was already running a complete platform on the
            web. It had:
          </p>
          <ul>
            <li>
              <strong>An admin portal</strong> for tracking maintenance tickets, queuing social
              media posts, approving Pro Spotlight features, and managing an anonymous
              suggestion box.
            </li>
            <li>
              <strong>A Pro tenant portal</strong>, login-gated by phone and name, where
              salon-suite Pros could check their suite, submit maintenance, refer a friend, and
              read studio events.
            </li>
            <li>
              <strong>A 13-page operations site</strong> covering home, suites, tickets,
              spotlights, suggestions, onboarding, facility, compliance, pipeline, financials,
              health, the Kira chat assistant, and tenant detail popups.
            </li>
            <li>
              <strong>Kira AI v1</strong> — voice input through the browser, voice output through
              a paid voice service, file attachments handled by an AI multimodal API,
              conversations remembered between sessions.
            </li>
            <li>
              <strong>30+ automated workflows</strong> handling lead routing, scheduled social
              posts, maintenance intake, tenant authentication, COI emails, and CRM sync.
            </li>
            <li>
              <strong>71 social media posts</strong> across three categories with 528 MB of
              video already loaded into the CRM's media library.
            </li>
          </ul>
          <p>
            April 7 was an emergency: a styling change broke every page on the operations site.
            Two back-to-back sessions rebuilt three of the pages from scratch. By the next
            morning everything was running again.
          </p>
          <p>
            The web platform proved the thesis: <em>one consolidated tool for the operator
            beats five disconnected ones.</em> But the low-code builder was capped at $80/month
            and 500 monthly AI credits — credits that ran out in week one. Every new tenant or
            workflow burned a credit. The architecture was running on rented rails.
          </p>
          <Callout>
            "Everything I need to change is part of a workflow." — Peter Lewis, 2026-04-25
          </Callout>
          <p>
            That sentence, recorded in a session note three weeks later, became the diagnosis
            for everything that followed. But first, a different bet had to ship.
          </p>

          <details className="mt-8 group">
            <summary className="cursor-pointer text-sm font-semibold text-brand-slate hover:text-image-pink select-none list-none flex items-center gap-2">
              <span className="inline-block transition-transform group-open:rotate-90">▸</span>
              Engineering details
            </summary>
            <div className="mt-4 pl-4 border-l-2 border-brand-mist space-y-4 text-sm text-brand-slate">
              <p>
                The web stack was BASE44 (low-code, $80/month Starter plan, 500
                AI credits/month) for the admin portal and Pro Hub, plus
                hand-coded HTML pages served by GoHighLevel's site builder for{' '}
                <code>pkntco.com</code>. The 13-page operations platform was a
                set of <code>pkntco-{`{home,suites,tickets,spotlights,suggestions,onboarding,facility,compliance,pipeline,dash,ops,kira,tenantDetail}`}.html</code>{' '}
                pages, each loading data via XHR against BASE44's REST entity
                API. <code>KiraChat.jsx</code> was 1,100+ lines: browser{' '}
                <code>SpeechRecognition</code> for input, ElevenLabs Starter
                plan ($5/mo, Rachel voice) for output, Anthropic Claude for
                completions and multimodal vision, <code>localStorage</code>{' '}
                for conversation persistence, PWA manifest with{' '}
                <code>display: standalone</code> for "Add to Home Screen."
              </p>
              <p>
                The 30+ n8n workflows ran on a Fly.io-hosted self-managed
                instance ($20/mo). Notable ones: <code>Process Website Lead</code>{' '}
                (3-tier validation + tag routing), <code>Route Lead by Preference v3</code>{' '}
                (single upsert with no silent drops), <code>Process Referral Form v3</code>{' '}
                (Zoho + GHL with $500 payout tracking), <code>Sync Lead Update</code>{' '}
                (Zoho field changes propagating to GHL + Quo), the four{' '}
                <code>kira-*</code> tool proxies (<code>send-sms</code>,{' '}
                <code>create-task</code>, <code>move-stage</code>, <code>send-slack</code>),
                and a Spotlight publish chain that uploaded photos to GHL
                Media Library and scheduled them through GHL's Social Planner.
              </p>
              <p>
                The April 7 emergency: a Theme Spec change deployed via GHL's
                visual builder stripped the <code>X-Webhook-Key</code> auth
                header from every <code>fetch</code> call across the site,
                broke 4-of-5 API field mappings (the Theme Spec also
                rewrote certain attribute names), and surfaced a JavaScript
                sort bug in the Suites table that had been latent since the
                page launched. Two back-to-back sessions rewrote{' '}
                <code>pkntco-suites.html</code> (sort + filter + theme
                isolation), <code>pkntco-home.html</code> (data hydration
                race), and <code>pkntco-kira.html</code> (chat composer
                positioning under GHL's wrapper CSS) from scratch.
              </p>
            </div>
          </details>
        </Phase>

        {/* Phase 2 — iOS, the bet that worked */}
        <Phase
          number="2"
          title="iOS — the bet that worked"
          subtitle="April 17 — April 22"
          tag="The danger point"
        >
          <p>
            <strong>This was the real risk in the rebuild.</strong> Building a native iPhone
            app isn't a casual choice for a one-person platform. Everything in the stack so far
            had been web-first — HTML, JavaScript, browsers. A native iPhone app meant a new
            toolchain, a new programming language, a new way to push releases (TestFlight and
            App Store review), and zero fallback if the architecture didn't hold. A bad bet
            here would have stalled the project for a month.
          </p>
          <p>
            <strong>It held.</strong>
          </p>
          <p>
            On April 17, the iOS code repository opened as an empty scaffold.{' '}
            <strong>April 18 alone shipped 109 commits.</strong> April 20 shipped another 94. By
            April 22, five days in, the iPhone app had tabs for tenants, suites, financials,
            maintenance tickets, onboarding, voice-driven Kira chat, and a floor plan view
            rendering 28 suites with color-coded occupancy.
          </p>
          <p>
            On April 20, late at night, a single document landed in the repository: a backend
            migration plan. The headline:
          </p>
          <Callout>
            "The Hub already reads and writes the data layer directly and works. iOS is going to
            do the same thing. The local cache becomes pure offline storage, the cloud-sync
            service stops being load-bearing."
          </Callout>
          <p>
            That decision retired four weeks of cloud-sync architecture in two paragraphs. The
            iPhone app would no longer be a peer with its own durability — it would be a client
            to the platform. The pivot took less than 48 hours to ship.
          </p>

          <h4 className="font-display text-xl text-brand-deep mt-10 mb-3">
            What that risk bought
          </h4>
          <p>
            Five weeks later, the payoffs from going native are the things that make the
            platform durable enough to scale nationally:
          </p>
          <ul>
            <li>
              <strong>A UI that runs disconnected.</strong> The operator can be in the basement
              checking a breaker, in a tenant's suite with no signal, on a plane between
              locations — the app keeps working from data cached on the device. Sync resumes
              when the network returns. Web apps cannot do this without significant additional
              engineering.
            </li>
            <li>
              <strong>Apple Intelligence on-device — free, private, no per-question cost.</strong>{' '}
              The Kira assistant runs against Apple's local AI engine when in Fast mode. No
              subscription. No round-trip to a paid API. No usage cap. The same kind of model
              that costs $20/month elsewhere is built into the device the operator already
              paid for.
            </li>
            <li>
              <strong>Outage immunity.</strong> When the automation server has a hiccup (which
              it does — it's a third-party hosted runtime), the iPhone app keeps working from
              cached data. The web Hub goes down with the automation server. The iPhone app
              doesn't.
            </li>
            <li>
              <strong>Vision Pro for nearly free.</strong> The same iPhone source code compiles
              to Apple Vision Pro with adaptive layouts, a translucent glass-pane window, hover
              effects, and a sidebar navigation that appears automatically. The operator can run
              the full platform from a spatial workspace with no separate app to maintain.
            </li>
            <li>
              <strong>Face ID, push notifications, deep system integration.</strong> Things the
              web can approximate but never quite match. Biometric unlock for the operator
              dashboard so a misplaced device doesn't expose tenant data. Silent push that
              triggers data sync without the app needing to be open. Full search integration.
              Universal clipboard. Handoff between iPhone and iPad.
            </li>
            <li>
              <strong>App Store credibility.</strong> "Available on the App Store" reads as a
              real product to a corporate executive. "Visit our web app" doesn't carry the same
              weight. The Apple review process itself is a quality gate the platform now passes.
            </li>
          </ul>
          <p>
            The web Hub still exists — and it's where Pros live, where forms live, where public
            surfaces live. But the operator's primary surface is now native, and the bet that
            took five days to ship has compounded into a category of capabilities the web can't
            match.
          </p>

          <details className="mt-8 group">
            <summary className="cursor-pointer text-sm font-semibold text-brand-slate hover:text-image-pink select-none list-none flex items-center gap-2">
              <span className="inline-block transition-transform group-open:rotate-90">▸</span>
              Engineering details
            </summary>
            <div className="mt-4 pl-4 border-l-2 border-brand-mist space-y-4 text-sm text-brand-slate">
              <p>
                The toolchain shift: Xcode 16 + iOS 26 SDK, Swift 5.10, SwiftUI
                + SwiftData (the new declarative persistence framework that
                replaced Core Data), CloudKit private database for cross-device
                sync, TestFlight for internal distribution, App Store Connect
                for review submission. Repo: <code>github.com/plewis-pknt/pknt-ops-ios</code>,
                module name <code>PKNTOps</code>, deployment target iOS 26.0 to
                pick up Foundation Models + Apple Intelligence on-device.
              </p>
              <p>
                The April 17–22 sprint shipped: tab-based <code>NavigationStack</code>{' '}
                root with Home / Dash / Suites / Ops / Agent surfaces, a{' '}
                <code>Base44Client</code> Swift wrapper around BASE44's REST
                entity API (read + write, optimistic local mirror through{' '}
                <code>@Model</code> SwiftData entities), CSV imports for studio
                pricing + rent ledger + Buildium GL transactions through a{' '}
                <code>SourceCSV</code> entity that hashed each row's content
                for change detection, APNs silent push from BASE44 →{' '}
                <code>BackgroundTenantSync</code> for cross-device coherence,
                a Kira agent with 33 registered tools (17 read, 16 write) all
                gated through a confirmation queue stored in{' '}
                <code>PendingActionStore</code>, and a{' '}
                <code>FloorPlanLayout</code> static defining the 28-suite
                Sharon floor plan as <code>(suiteNumber, x, y, width, height)</code>{' '}
                tuples that <code>FloorPlanView</code> rendered as color-coded
                rectangles based on occupancy state.
              </p>
              <p>
                The April 20 pivot — <code>BACKEND_MIGRATION_PLAN.md</code> —
                killed CloudKit as the canonical store. Before: SwiftData
                writes locally, CloudKit syncs to other devices, BASE44 was a
                read-only data feed. Problem: CloudKit's eventual consistency
                meant a write on iPad could take 30–90 seconds to land on
                iPhone, and the silent-push fan-out from BASE44 would race the
                CloudKit fan-out, producing duplicate entries and
                conflict-resolution edge cases that consumed two weeks of
                debugging.
              </p>
              <p>
                After the pivot: BASE44 is the canonical store (and later,
                Supabase). SwiftData becomes a pure offline cache. Every
                domain entity got a{' '}
                <code>BackgroundXxxSync</code> service (
                <code>BackgroundTenantSync</code>,{' '}
                <code>BackgroundSuiteSync</code>,{' '}
                <code>BackgroundOnboardingSync</code>, etc.) that fetched on{' '}
                <code>scenePhase == .active</code>, wrote through a local{' '}
                <code>@Model</code> mirror, and emitted a{' '}
                <code>NotificationCenter</code> change event for any view
                observing it. CloudKit still configured for cross-device key
                material in Keychain, but no longer load-bearing for app data.
                Reads stayed local-first; writes went through{' '}
                <code>Base44Client.updateEntity</code> / <code>createEntity</code>{' '}
                with optimistic UI and rollback on HTTP error.
              </p>
            </div>
          </details>
        </Phase>

        {/* Phase 3 */}
        <Phase
          number="3"
          title="The pivot to Supabase"
          subtitle="April 23 — April 26"
          tag="The big rebuild"
        >
          <p>
            On April 23, a second backend code repository opens — a fresh database backend.
            The next day, April 24, a third repository: a modern web application that will
            replace the BASE44-era Pro Hub. And a 9 KB document captures the architecture for
            scaling nationally in one paragraph:
          </p>
          <Callout>
            <strong>"IMAGE builds a platform. Owners run locations on it. Pros use it if they
            want. Corporate talks to owners; owners talk to Pros."</strong> Three distinct user
            experiences on one shared backbone. IMAGE controls the platform features, brand, and
            rollout cadence. Each franchise location's data is isolated from every other's at
            the database level. <em>Adding location 151 changes zero lines of code.</em>
          </Callout>
          <p>
            On April 25, a 250-line architectural decision document lands titled{' '}
            <em>"GoHighLevel becomes a dumb pipe. Workflow logic moves to the platform's own
            backend."</em> The diagnosis is the cleanest sentence in the entire month-long arc:
            the CRM was doing three jobs at once — handling text messages, holding the
            authoritative contact records, and running the business's automated workflows. The
            first two were keepers. The third — 34 opaque, undocumented workflows that drove
            every drip campaign, reminder, and follow-up sequence — was the
            franchise-replication blocker. There was no way to export them, no version control,
            no way to test changes, no audit log of what fired when. Reproducing the Sharon
            setup at a new location was a multi-week manual exercise with no confidence the
            result actually matched.
          </p>
          <p>
            The decision: tags driving CRM workflows become real database columns. Workflows
            become real code that runs on the platform's own backend. The CRM keeps the text
            messaging pipe and the calendar; the brain moves to the platform. Every recurring
            action gets put in version control, on a test path, with an audit log of every
            send.
          </p>
          <p>
            By April 26, three days after that decision, the migration's first wave shipped:
            five tag-driven email functions (request COI, send welcome letter, application
            approved, welcome package, COI received confirmation) running off a queue drained
            every minute. Every step logged for audit.{' '}
            <strong>Twelve of the 34 CRM workflows became deletable</strong> — 35% of the
            sub-account, retired into 5 small functions and one queue.
          </p>

          <details className="mt-8 group">
            <summary className="cursor-pointer text-sm font-semibold text-brand-slate hover:text-image-pink select-none list-none flex items-center gap-2">
              <span className="inline-block transition-transform group-open:rotate-90">▸</span>
              Engineering details
            </summary>
            <div className="mt-4 pl-4 border-l-2 border-brand-mist space-y-4 text-sm text-brand-slate">
              <p>
                The two new repositories: <code>image-studios-backend</code>{' '}
                (Supabase project, Postgres 17, Deno-based Edge Functions, RLS
                policies for tenant isolation) and{' '}
                <code>image-hub-web</code> (React + Vite + Tailwind v4 +
                TypeScript, deployed via Cloudflare Pages with the{' '}
                <code>pkntops.com</code> apex). The architectural decision was{' '}
                <code>ADR-023.md</code> — "GHL becomes a dumb pipe. Workflow
                logic moves to Supabase."
              </p>
              <p>
                The three jobs GHL was doing: <strong>(1)</strong> SMS / email
                channel through Twilio + Mailgun under GHL's hood;{' '}
                <strong>(2)</strong> contact-of-record with custom fields,
                tags, opportunity pipeline, calendar; <strong>(3)</strong> a
                visual workflow engine of 34 sub-account workflows triggered
                by tag adds, opportunity stage changes, form submissions, and
                webhook events. Jobs 1 and 2 stay; job 3 migrates.
              </p>
              <p>
                The April 26 first tranche: five SQL RPCs —{' '}
                <code>request_coi(p_tenant_id)</code>,{' '}
                <code>send_welcome_letter(p_tenant_id)</code>,{' '}
                <code>send_application_approved(p_tenant_id)</code>,{' '}
                <code>send_welcome_package(p_tenant_id)</code>,{' '}
                <code>send_coi_received_confirmation(p_tenant_id)</code> —
                each enqueueing rows to a{' '}
                <code>scheduled_messages</code> table with{' '}
                <code>send_at</code> timestamps. A{' '}
                <code>workflow-dispatcher</code> Edge Function runs every
                minute on <code>pg_cron</code>, dequeues due rows, calls
                Resend for email or GHL's <code>conversations/messages</code>{' '}
                for SMS, and writes the result (<code>sent</code>,{' '}
                <code>failed</code>, <code>skipped_by_goal</code>) back to{' '}
                <code>workflow_audit_log</code>. Idempotency keyed on{' '}
                <code>(tenant_id, workflow_kind, scheduled_at)</code>.
              </p>
              <p>
                The 12 retired workflows mapped to: 4 onboarding-stage
                triggers (Application → Lease Signing → Insurance →
                Pre-Move-In notifications), 5 COI lifecycle workflows
                (request, reminder days 3 / 7 / 14, received confirmation),
                2 welcome-package sends (lease-signed + move-in-day-minus-3),
                and 1 application-approved auto-reply. Each retirement
                followed the same pattern: identify the trigger, write the
                SQL RPC, configure the queue entry, run the test path, delete
                the GHL workflow, monitor the audit log for one full
                stage-cycle.
              </p>
            </div>
          </details>
        </Phase>

        {/* Phase 4 — The new Hub on Supabase */}
        <Phase
          number="4"
          title="The Hub, rebuilt"
          subtitle="April 24 — April 30"
          tag="Pro-facing surface, take two"
        >
          <p>
            Same week as the backend decision, a new web application opens. This is the modern
            replacement for the BASE44-era Pro Hub — same purpose (the place where salon-suite
            Pros submit maintenance tickets, sign their certificates of insurance, refer
            friends, read studio events), but built on the new platform.
          </p>
          <p>
            In nine active development days, the new Hub picked up: passwordless sign-in tied
            to the same auth as the iPhone app, tenant info and lease details pulled from the
            same database the iPhone reads, maintenance ticket capture with photo upload, a
            certificate-of-insurance form with PDF generation, Pro Spotlight feature
            submissions, refer-a-friend with reward tracking, and an admin Quality &amp;
            Operations Portal cataloguing 223 test cases and 34 due-diligence artifacts.
          </p>
          <p>
            <strong>62 commits, around 8,500 lines of code.</strong> Same backend as the iPhone
            app — different surface. The "build once, use everywhere" thesis now visible at a
            glance: two surfaces (one native, one web), one database, one auth provider, one
            set of business rules. Add a third surface (a corporate dashboard, a lobby
            kiosk, a tablet-only experience) and it inherits everything for free.
          </p>

          <details className="mt-8 group">
            <summary className="cursor-pointer text-sm font-semibold text-brand-slate hover:text-image-pink select-none list-none flex items-center gap-2">
              <span className="inline-block transition-transform group-open:rotate-90">▸</span>
              Engineering details
            </summary>
            <div className="mt-4 pl-4 border-l-2 border-brand-mist space-y-4 text-sm text-brand-slate">
              <p>
                Stack: React 18 + Vite 5 + TypeScript 5.6 + Tailwind 4 (the new
                CSS-first variant), React Router for navigation, Supabase JS
                client (<code>@supabase/supabase-js</code>) for data + auth.
                Built static and deployed via Cloudflare Pages with{' '}
                <code>pkntops.com</code> as the apex domain. Magic-link OTP
                via Supabase Auth wired through the same{' '}
                <code>auth.users</code> table the iOS app uses, with{' '}
                <code>operator_users</code> joined on{' '}
                <code>auth_user_id</code> for role/location-access metadata.
              </p>
              <p>
                Surfaces shipped: <code>/login</code>,{' '}
                <code>/dashboard</code>, <code>/maintenance</code> (capture
                form with photo upload to{' '}
                <code>supabase.storage</code> bucket{' '}
                <code>maintenance-photos</code>), <code>/coi</code> (COI
                upload form with multimodal Anthropic verification, PDF
                generation via <code>jspdf</code>),{' '}
                <code>/spotlight</code> (Pro feature intake), <code>/refer</code>{' '}
                (referral form with $500 payout tracking),{' '}
                <code>/admin/qa</code> (Quality &amp; Operations Portal —
                test management + defect tracking + artifact index, 5 tabs:
                Dashboard / Cases / Runs / Defects / Artifacts, gated on{' '}
                <code>operator_users.role = 'corporate_admin'</code>),{' '}
                <code>/admin/sequences</code> (read-only view of the email
                cadence engine), and the standalone marketing/legal pages{' '}
                <code>/build-story</code>, <code>/privacy</code>,{' '}
                <code>/terms</code>.
              </p>
              <p>
                The Hub doesn't bake any backend secrets — every privileged
                operation is an Edge Function call. The single client-side
                bearer token is the n8n webhook key (a deliberate choice;
                rate-limit gate against random crawlers, not a
                confidentiality boundary; the webhook routes themselves
                authenticate the caller via Supabase JWT). Every page reads
                via Postgres-with-RLS so a malicious user can't see another
                location's data even if they edit the URL.
              </p>
            </div>
          </details>
        </Phase>

        {/* Phase 5 */}
        <Phase
          number="5"
          title="Multi-tenancy lands"
          subtitle="April 27 — May 1"
          tag="Two more locations"
        >
          <p>
            The first non-Sharon location, <strong>Brookline</strong>, came online April 27. 40
            suites, 21 occupied, a distinct three-block floor plan, real backend data, full
            iPhone rendering. Adding the location was about 50 lines of code — a floor plan
            entry, a settings tab entry, and one database seed.
          </p>
          <p>
            That was the proof. Same iPhone app, same Hub, same backend — three locations now
            (Sharon, Brookline, Demo Studios), each with their own sender identity, branding,
            and data, all reading from the same shared engine.
          </p>
          <p>
            On May 1, six pull requests landed in one session: a three-tier role hierarchy
            (corporate admin / location owner / operator), per-location personalization for the
            Kira assistant (each location can pick a custom name and photo), the
            credential-management screen split into a per-location view and a system view,
            secrets hygiene (legacy tokens cleared from the codebase), production bug fixes
            for facility-calendar persistence and rent-roll save mismatches, and a Google
            Drive → AI matching → social-media-draft pipeline that ports a legacy automation
            into a single backend function.
          </p>

          <details className="mt-8 group">
            <summary className="cursor-pointer text-sm font-semibold text-brand-slate hover:text-image-pink select-none list-none flex items-center gap-2">
              <span className="inline-block transition-transform group-open:rotate-90">▸</span>
              Engineering details
            </summary>
            <div className="mt-4 pl-4 border-l-2 border-brand-mist space-y-4 text-sm text-brand-slate">
              <p>
                Brookline's 50-line addition: a{' '}
                <code>FloorPlanLayout.brookline_ma</code> static enum case
                holding (suiteNumber, x, y, width, height) tuples for each of
                40 suites across three building blocks; a{' '}
                <code>case .brookline_ma</code> branch in{' '}
                <code>SettingsView.locationPicker</code>; one INSERT into{' '}
                <code>locations</code> with the{' '}
                <code>brookline_ma</code> id; one INSERT into{' '}
                <code>location_configs</code> with display_name, address,
                phone, owner identity, hub branding JSON; and operator-user
                seeding to give Phil (the Brookline-only operator) access.
                Tenant data was loaded from Brookline's exported Buildium
                rent ledger via the same{' '}
                <code>SourceCSV → BuildiumCSVParser → tenant_imports</code>{' '}
                pipeline that hydrates Sharon.
              </p>
              <p>
                Tenant isolation is enforced by Postgres Row-Level Security
                (RLS). Every domain table (<code>tenants</code>,{' '}
                <code>suites</code>, <code>maintenance_tickets</code>, etc.)
                has policies keyed off <code>location_id = current_location()</code>{' '}
                or <code>location_id = ANY(operator_locations())</code> —
                helper functions that read the active operator's{' '}
                <code>operator_users.location_access[]</code> array. As of
                this phase the policies are wired but a parallel
                "anon-overlay" still allows broader reads for legacy
                code-paths still being migrated; RLS becomes the sole gate
                in a Phase 4 follow-up (post-public-launch hardening).
              </p>
              <p>
                The May 1 six-PR session mapped to: <strong>(1)</strong>{' '}
                <code>operator_users.role</code> CHECK constraint allowing
                exactly <code>corporate_admin</code> / <code>location_owner</code> /{' '}
                <code>operator</code>, plus Postgres helpers{' '}
                <code>app_can_admin_location(text)</code> and{' '}
                <code>app_can_edit_agent(text)</code>;{' '}
                <strong>(2)</strong> two new <code>location_configs</code>{' '}
                columns <code>assistant_name</code> and{' '}
                <code>assistant_photo_path</code>, plus an{' '}
                <code>assistant-photos</code> Storage bucket;{' '}
                <strong>(3)</strong> Manage Keys split into{' '}
                <code>LocationKeysView</code> (per-location read-only summary
                with secret fingerprinting) and{' '}
                <code>SystemKeysView</code> (corp-admin Keychain inspector
                for Anthropic / ElevenLabs / Resend / GHL / SB);{' '}
                <strong>(4)</strong> Base44 service-role token cleared from{' '}
                <code>Secrets.swift</code> (workspace had been unpublished),
                Keychain bump-guard wiping identity slots on build version
                bump to prevent TF carry-over of stale credentials;{' '}
                <strong>(5)</strong> facility-calendar Supabase upsert lookup
                fixed (was querying non-existent{' '}
                <code>scheduled_month/_year</code> columns), and Suite-save
                <code>rent_monthly</code> → <code>monthly_rent</code> rename
                that had been producing 41 errors per Sync All push;{' '}
                <strong>(6)</strong> the{' '}
                <code>social-monthly-scheduler</code> Edge Function
                replacing a legacy n8n workflow, walking a Google Drive
                folder + matching posts to media via Claude Sonnet 4 +
                creating GHL Social Planner drafts with images and videos
                attached.
              </p>
            </div>
          </details>
        </Phase>

        {/* Phase 6 */}
        <Phase
          number="6"
          title="App Store ready"
          subtitle="May 5 — May 6"
          tag="Submission week"
        >
          <p>
            Two days of focused polish to clear the App Store submission bar. Apple's privacy
            manifest declared. Encryption export compliance noted. All the system permission
            prompts wired in (camera, photos, microphone, speech recognition, Face ID,
            location). The 1024-pixel icon set complete with light, dark, and tinted variants.
            The version number now reads dynamically from the build configuration — no more
            "<code>0.1 · internal build</code>" hardcoded forever. Six pull requests of
            admin-panel cleanup including a Vision Pro polish pass that lands native window
            sizing, translucent glass backgrounds, and a fix to the sign-in flow so Apple's
            reviewer can log in cleanly on Vision Pro.
          </p>
          <p>
            Public Privacy Policy and Terms of Service went live at{' '}
            <code>pkntops.com/privacy</code> and <code>pkntops.com/terms</code> on May 6 —
            required by both Apple's reviewer and Google's sign-in consent screen. Same day, a
            demo account for the Apple reviewer was pre-staged in the platform as a location
            owner of Demo Studios, and the new email-cadence engine was end-to-end verified: 6
            emails queued, day-0 email sent in 35 seconds, and the "exit early when the goal is
            reached" rule verified by manually adding the <code>tour-booked</code> tag
            mid-cadence and watching the next scheduled send mark itself{' '}
            <em>"skipped because the goal was reached."</em>
          </p>
          <p>
            That last test was the proof that the migration's most ambitious goal —{' '}
            <em>"every workflow is data, not CRM config"</em> — actually works.
          </p>

          <details className="mt-8 group">
            <summary className="cursor-pointer text-sm font-semibold text-brand-slate hover:text-image-pink select-none list-none flex items-center gap-2">
              <span className="inline-block transition-transform group-open:rotate-90">▸</span>
              Engineering details
            </summary>
            <div className="mt-4 pl-4 border-l-2 border-brand-mist space-y-4 text-sm text-brand-slate">
              <p>
                The submission checklist that landed: a{' '}
                <code>PrivacyInfo.xcprivacy</code> manifest declaring API
                usage and tracking domains; an{' '}
                <code>ITSAppUsesNonExemptEncryption = false</code> in{' '}
                <code>Info.plist</code> for the export compliance question;
                purpose strings for{' '}
                <code>NSCameraUsageDescription</code>,{' '}
                <code>NSPhotoLibraryUsageDescription</code>,{' '}
                <code>NSMicrophoneUsageDescription</code>,{' '}
                <code>NSSpeechRecognitionUsageDescription</code>,{' '}
                <code>NSFaceIDUsageDescription</code>, and{' '}
                <code>NSLocationWhenInUseUsageDescription</code>; a 1024px
                <code>AppIcon.appiconset</code> with light, dark, and tinted
                variants per Apple's iOS 18+ visual identity guidelines;{' '}
                <code>CFBundleShortVersionString</code> +{' '}
                <code>CFBundleVersion</code> read at runtime so the Settings
                "About" screen shows the same version the App Store does.
              </p>
              <p>
                The visionOS polish PR landed: native window-size constraints
                via <code>WindowGroup</code> defaults, a glass background
                using <code>.background(.ultraThinMaterial)</code>, hover
                effects on every primary button (<code>.hoverEffect(.lift)</code>),
                and a sidebar navigation auto-shown on regular size class.
                The 4-instance OAuth fix: the Google Sign-In SDK was
                creating four ASWebAuthenticationSession instances on Vision
                Pro instead of one (a known SDK bug under multi-window
                contexts) — patched by gating session creation on
                <code>UIApplication.shared.connectedScenes.first?.session.role == .windowApplicationSpatial</code>{' '}
                with single-shot semantics.
              </p>
              <p>
                The May 6 verification of the universal email cadence:
                the <code>enroll_lead_drip</code> RPC accepts
                <code>(p_location_id, p_recipient_email, p_cadence_kind,
                p_goal_tag)</code> and inserts 6 rows into{' '}
                <code>scheduled_messages</code> with{' '}
                <code>send_at</code> values at days 0, 1, 3, 5, 9, 16. The{' '}
                <code>workflow-dispatcher</code> Edge Function (cron'd
                every minute via <code>pg_cron</code>) drains due rows,
                checks if the recipient has acquired the goal tag (
                <code>tour-booked</code>) since enrollment by querying
                GHL's contact API, and either sends via Resend or marks
                the row <code>skipped_by_goal</code>. The end-to-end test
                added the goal tag manually mid-cadence and confirmed the
                next scheduled send wrote{' '}
                <code>{`{status: 'skipped_by_goal', skipped_at: <ts>}`}</code>{' '}
                back to <code>workflow_audit_log</code> instead of
                firing.
              </p>
            </div>
          </details>
        </Phase>

        {/* Phase 7 — Readiness phase */}
        <Phase
          number="7"
          title="The pause before publishing"
          subtitle="May 7 — single day"
          tag="Readiness phase"
        >
          <p>
            Yesterday's plan said cut a TestFlight build. A morning question —{' '}
            <em>"what does a fresh user actually see when they install this?"</em> —
            turned into a full audit. Looking at the platform through three lenses
            (security, personal data, multi-location safety) surfaced 22 things that
            needed fixing before any build went out.
          </p>
          <p>
            The first finding: a credential the platform uses to talk to its CRM was
            sitting in plain text inside the iOS app file, inside 30 backend automation
            scripts, and shipping openly in the public website's bundled code. Anyone
            determined could already pull it from the website, but having it in three
            different places meant a rotation had to coordinate across all three. A
            single-day rotation closed it: a new credential created, the secure
            server-side store updated (which immediately covered 12 backend functions),
            30 automation scripts bulk-edited through their API, the iOS app rewritten
            so it no longer carries the credential at all, and the old credential
            revoked. Old credential verified dead, new credential verified live, end to
            end on real production traffic.
          </p>
          <p>
            A first TestFlight build hitting a real device surfaced seven user-facing
            bugs in quick succession — Kira's voice would play silent, the microphone
            button would freeze the screen, the tap-to-call phone button stopped
            working, the keyboard needed three or four taps to focus, the Send arrow on
            the keyboard didn't actually send anything, a mic icon was duplicated
            between iOS's keyboard and the chat composer, and the assistant introduced
            herself as "Kira" even for an operator who'd renamed her to "Nia." A
            defensive rewrite of the audio system, switching to a simpler text field
            that dodges a known iOS 26 quirk, and a cleanup of the chat composer fixed
            all seven in one change.
          </p>
          <Quote source="Operator decision, late afternoon">
            I need everything in the software fixed before shipping. It's ok to delay. I
            do not want to explain why a feature is not working after I shipped it.
          </Quote>
          <p>
            That single line reframed the rest of the day. Internal testing of the
            platform's on-device AI assistant — running locally on the operator's
            iPhone, no cloud, no per-question cost, the platform's signature
            differentiator — had confidently taken a pizza order, then repeated the
            previous reply word-for-word, then appended a phantom Q&amp;A about
            occupancy rate. The differentiator was a credibility risk, not a
            credibility win.
          </p>
          <p>Eight focused changes over the rest of the day, each shipped as its own pull
            request:</p>
          <ul className="list-disc pl-6 space-y-2 my-4">
            <li>
              A tightened set of instructions telling Kira to refuse off-topic requests
              ("ordering food isn't something I can help with — want me to look up a
              tenant instead?") with concrete refusal examples for the model to mimic.
            </li>
            <li>
              A fix to give Kira a real conversation memory, so follow-up questions
              like <em>"what about her sister?"</em> carry the prior context properly
              instead of starting fresh each time.
            </li>
            <li>
              A move from keyword-guessing to a typed menu of data lookups (look up
              tenant, look up suite, list late tenants, financial summary, onboarding
              pipeline, full rent roll). Kira picks which to call herself, instead of
              the app trying to predict it from the question's wording.
            </li>
            <li>
              A diagnostics screen inside the app — Settings → Kira Diagnostics — that
              shows the last 50 turns: what was asked, what was answered, how long it
              took, whether anything was flagged.
            </li>
            <li>
              A safety net that catches the worst failure modes after the model
              responds (phantom Q&amp;A, repeating the previous answer, engaging with
              off-topic requests) and replaces them with a polite redirect before they
              reach the operator.
            </li>
            <li>
              Automatic escalation to the cloud-based "Deep" model when a question is
              too complex for the on-device version — multi-part questions, comparisons,
              "explain why" phrasings — so cost stays predictable but quality stays
              reliable.
            </li>
            <li>
              Streaming responses, so the reply reveals word-by-word as Kira generates
              it. Same UX as iMessage's Smart Reply or ChatGPT.
            </li>
            <li>
              A pass on the bundled knowledge files removing every personal phone
              number, owner name, and brand reference, replacing them with placeholders
              that fill in per-location at read time. The iOS file no longer ships any
              real personal data.
            </li>
          </ul>
          <p>
            The same chat that confidently took three pepperoni pizzas in the morning
            now refuses out-of-scope questions cleanly, grounds every answer in real
            data, streams its reply word-by-word, and tags every turn for inspection.
            An Apple reviewer signing in to the demo location sees a "Studio Manager"
            persona instead of the operator's real name — because every template now
            fills in from the live database, not from text baked into the app.
          </p>
          <Callout>
            Thirteen pull requests in one day, all merged. The TestFlight build waited
            until each one was verified on a real device. Engineering credibility lives
            in what doesn't get shipped as much as what does.
          </Callout>

          <details className="mt-8 group">
            <summary className="cursor-pointer text-sm font-semibold text-brand-slate hover:text-image-pink select-none list-none flex items-center gap-2">
              <span className="inline-block transition-transform group-open:rotate-90">▸</span>
              Engineering details
            </summary>
            <div className="mt-4 pl-4 border-l-2 border-brand-mist space-y-4 text-sm text-brand-slate">
              <p>
                The leaked credential was a GoHighLevel Personal Integration Token,
                hardcoded as <code>pit-3e4f0afe-…</code> in{' '}
                <code>iOS/GHLClient.swift:39</code> and{' '}
                <code>iOS/GHLTenantImportService.swift:120</code>, embedded in the{' '}
                <code>Authorization: Bearer …</code> header of HTTP request nodes
                across 30 active n8n workflows, and shipped in{' '}
                <code>image-hub-web/src/lib/n8n.ts:14</code> as a "client-side bearer
                token" by design. Rotation: a new PIT (
                <code>pit-25c437a5-…</code>) created in GoHighLevel, the Supabase secret
                <code>GHL_API_KEY</code> updated via{' '}
                <code>supabase secrets set</code> (covering 12 Edge Functions on next
                cold start), 30 n8n workflows bulk-rotated via the n8n REST API
                (single-pass, idle finalizer skipped 6 archived workflows that the API
                refuses to update), iOS{' '}
                <code>GHLClient</code> + <code>GHLTenantImportService</code> refactored
                to call a new server-side <code>ghl-contact</code> Edge Function that
                holds the PIT, six new endpoints added to that function (GET single,
                GET tenants list, PUT update, plus the existing search), and the old
                PIT revoked in the GoHighLevel UI. Verification: <code>curl</code>{' '}
                against the old token returned <code>HTTP 401</code>, against the new
                returned <code>HTTP 200</code>.
              </p>
              <p>
                The seven TF-build bugs traced to two root causes: an{' '}
                <code>AVAudioSession</code> lifecycle that was calling{' '}
                <code>setActive(false, .notifyOthersOnDeactivation)</code> after
                listening, leaving the next <code>setActive(true)</code> hung
                (visible as <code>IPCAUClient -66748</code> in the device console);
                and an iOS 26 SwiftUI hit-test bug on{' '}
                <code>TextField(axis: .vertical)</code> with a custom rounded
                background, which dropped taps in the padding zone and treated Return
                as a newline regardless of <code>.submitLabel(.send)</code>. The fix:
                single-chokepoint{' '}
                <code>enterPlaybackSession()</code> / <code>enterRecordSession()</code>{' '}
                helpers replacing silenced <code>try?</code> calls; a watchdog
                <code>Task</code> auto-resetting <code>.speaking</code> after 60s if
                the completion delegate doesn't fire; <code>play()</code> return value
                checked (it was being ignored); MP3 sanity check before{' '}
                <code>AVAudioPlayer</code> init; switch to single-line{' '}
                <code>TextField</code> for reliable <code>.onSubmit</code>; removal of
                the redundant in-composer mic and send buttons in favor of iOS's
                native dictation mic and Return-as-Send.
              </p>
              <p>
                The eight AI reliability steps, in order:{' '}
                <strong>(1)</strong> rewrite{' '}
                <code>baseInstructions</code> with a SCOPE section, three concrete
                refusal examples, and an explicit Q&amp;A-format ban —{' '}
                <a href="https://github.com/plewis-pknt/pknt-ops-ios/pull/23"
                  className="underline hover:text-image-pink">PR #23</a>;{' '}
                <strong>(2)</strong> convert{' '}
                <code>AppleBrain</code> from a stateless <code>enum</code> to a{' '}
                <code>@MainActor final class</code> with a per-chat{' '}
                <code>LanguageModelSession</code> in <code>sessionStorage</code>,
                resetting on <code>startNewChat()</code>, dropping the lossy "PRIOR:"
                text-block manual history workaround — same PR;{' '}
                <strong>(3)</strong> six{' '}
                <code>Tool</code>-protocol conformances in{' '}
                <code>KiraAppleTools.swift</code> wrapping{' '}
                <code>KiraRetrieval</code> slices, registered at session creation via{' '}
                <code>LanguageModelSession(tools:instructions:)</code>, dropping the
                keyword classifier in <code>assembleContext</code> and the{' '}
                <code>DATA:</code> prefetch block —{' '}
                <a href="https://github.com/plewis-pknt/pknt-ops-ios/pull/24"
                  className="underline hover:text-image-pink">PR #24</a>;{' '}
                <strong>(4)</strong>{' '}
                <code>AppleBrainTelemetry</code> ring buffer (50 entries, in-memory)
                +{' '}
                <code>KiraDiagnosticsView</code> in Settings → System (corp-admin
                gated) —{' '}
                <a href="https://github.com/plewis-pknt/pknt-ops-ios/pull/25"
                  className="underline hover:text-image-pink">PR #25</a>;{' '}
                <strong>(5)</strong>{' '}
                <code>AppleBrainValidator</code> with four flags
                (<code>qa_format</code>, <code>repetition</code>, <code>off_topic</code>,
                <code>empty</code>), <code>lastAssistantText</code> tracking for the
                repetition check, and a safe-redirect fallback —{' '}
                <a href="https://github.com/plewis-pknt/pknt-ops-ios/pull/26"
                  className="underline hover:text-image-pink">PR #26</a>;{' '}
                <strong>(6)</strong>{' '}
                <code>AppleBrainEscalation.shouldPreEscalate(_:)</code> heuristic
                (&gt;25 words, analytical keywords, 3+ multi-clause tokens) plus
                post-validation escalation, both routed through an injected{' '}
                <code>deepFallback</code> closure that calls the existing{' '}
                <code>agent.turn</code> n8n path —{' '}
                <a href="https://github.com/plewis-pknt/pknt-ops-ios/pull/27"
                  className="underline hover:text-image-pink">PR #27</a>;{' '}
                <strong>(7)</strong>{' '}
                <code>respondStreaming(to:context:deepFallback:onPartial:)</code>{' '}
                using <code>LanguageModelSession.streamResponse(to:)</code>, KiraView
                appends a placeholder message and updates its <code>text</code> in
                place via indexed mutation as snapshots arrive —{' '}
                <a href="https://github.com/plewis-pknt/pknt-ops-ios/pull/28"
                  className="underline hover:text-image-pink">PR #28</a>;{' '}
                <strong>(8)</strong> sed-templating across 14 bundled markdown files
                (<code>{`{{LOCATION_NAME}}`}</code>, <code>{`{{OWNER_NAME}}`}</code>,
                <code>{`{{OWNER_PHONE}}`}</code>, <code>{`{{LOCATION_ADDRESS}}`}</code>,{' '}
                <code>{`{{LOCATION_ADDRESS_STREET}}`}</code>),{' '}
                <code>KiraKnowledgeStore.refreshLocationContext()</code> priming a
                placeholders cache from{' '}
                <code>fetchLocationConfigSummary()</code>,{' '}
                <code>substitute(_:)</code> applied to every bundle/overlay read,{' '}
                <code>LocationConfigSummary</code> extended with{' '}
                <code>ownerDisplayName</code> + <code>ownerEmail</code>,{' '}
                <code>location_configs</code> rows updated for sharon_ma /
                demo_studios / brookline_ma —{' '}
                <a href="https://github.com/plewis-pknt/pknt-ops-ios/pull/29"
                  className="underline hover:text-image-pink">PR #29</a>.
              </p>
            </div>
          </details>
        </Phase>

        {/* Phase 8 — Managed Agents · Phase 1 */}
        <Phase
          number="8"
          title="The platform team, in agent form"
          subtitle="May 9–10 — overnight build"
          tag="Managed Agents · Phase 1"
        >
          <p>
            The App Store submission went out the door. The next question wasn't
            about the app — it was about how this runs when there are thirty
            franchise locations instead of three. One engineer running operations
            for the IMAGE Studios platform works fine today; it doesn't work at
            national scale.
          </p>
          <p>
            The answer turned out to be the platform itself, recursively. Twelve
            area-expert agents across three teams — one team that <em>investigates</em>{' '}
            problems as they come in, one that <em>implements</em> changes when they
            need to ship, and one that <em>verifies</em> every change before it goes
            live — backed by a Coordinator at the front door that classifies each
            incoming issue and routes it to the right specialist. Each agent serves
            every location simultaneously. Each one remembers what it learned from
            every issue it resolved, so the platform gets sharper every week without
            anyone explicitly training it.
          </p>
          <p>
            Designing the architecture took an evening. Building Phase 1 of the
            foundation took the overnight that followed. Multiple AI coding sessions
            worked in parallel — each one a specialist in its own piece, coordinated
            through structured work items and a paper-trail journal kept in git.
            Two of those sessions ran concurrently for hours, finishing different
            components on different feature branches, no collisions, no lost work.
            The same shape we're building the platform to run with at national scale
            is the shape we used to build it overnight.
          </p>
          <p>What landed in production this arc:</p>
          <ul className="list-disc pl-6 space-y-2 my-4">
            <li>
              <strong>Memory layer.</strong> Seven Postgres tables holding the agent
              registry, the compounding sanitized lessons the platform learns over
              time, per-investigation session context, and an append-only audit
              ledger. Every table protected by row-level security; every access
              path explicit.
            </li>
            <li>
              <strong>Sanitizer.</strong> Two-stage filter that every memory write
              passes through. Fast rules first (emails, phone numbers, location
              names, dollar amounts get stripped automatically), then a small
              classifier model reviews what's left for descriptions that could
              re-identify a specific franchise even after the obvious redactions.
              Borderline cases queue for a human to confirm before they enter
              the cross-location pattern store. Personal information never crosses
              franchises, by construction.
            </li>
            <li>
              <strong>Audit logging.</strong> Every action by every agent — every
              classification, every tool call, every change deployed — writes a
              row to a single ledger. Compliance-grade trail of who did what when.
              The same shape the operations team would build for a regulated SaaS
              business, baked in from day one.
            </li>
            <li>
              <strong>Tool inventory.</strong> When an agent needs to look up a
              tenant, a suite, or a payment, it goes through a tool that{' '}
              <em>physically can't</em> return data for the wrong franchise. The
              location boundary is enforced at the access layer, not by convention,
              so an agent can't accidentally leak Brookline's data into Sharon's
              investigation even if its prompt asked it to.
            </li>
            <li>
              <strong>Coordinator.</strong> The front door. A free-text problem
              report comes in — "the iOS app crashed on launch," "who's behind on
              rent at Sharon," "Pros can't sign in to the Hub" — and within a
              fraction of a second the Coordinator classifies the area, opens a
              session, records an audit entry, and routes it. The endpoint is live
              and answering real test traffic.
            </li>
          </ul>
          <p>
            By the time the sun came up, the full vertical slice was running
            against real production infrastructure. A problem report enters the
            Coordinator, gets classified, opens a session, lands an audit row,
            and waits for the agent specialist to take it from there. The agent's
            brain itself — the prompt that turns the platform into an actual
            iOS Support agent that diagnoses real bugs — is the next arc. The
            foundation it sits on top of is built.
          </p>
          <Callout>
            Phase 1 of the agent platform is live in production. 143 automated
            tests pass across the stack, all five components answer real traffic,
            and the framework that built it (work items, async question queue,
            engineer + coordinator journals, feature branches per task) held
            under sustained concurrent load. Two AI sessions, one human reviewer,
            zero collisions.
          </Callout>

          <details className="mt-8 group">
            <summary className="cursor-pointer text-sm font-semibold text-brand-slate hover:text-image-pink select-none list-none flex items-center gap-2">
              <span className="inline-block transition-transform group-open:rotate-90">▸</span>
              Engineering details
            </summary>
            <div className="mt-4 pl-4 border-l-2 border-brand-mist space-y-4 text-sm text-brand-slate">
              <p>
                <strong>Architecture decisions locked in this arc, by the
                numbers</strong>: 22 decisions in the design-session history
                table, captured in <code>PKNT_OPS_AGENTS_FRAMEWORK_DESIGN_2026-05-09.md</code>.
                The high-leverage ones: three teams not two (Support investigates ·
                Build implements · Test verifies, each in four areas — iOS, Backend,
                Hub, Integrations — for twelve agents plus one Coordinator);
                corporate-tier-only specialists (not per-location · scale by adding
                sibling agents within a team, not by sharding across franchises);
                two-layer memory split between session-scoped PII that auto-purges
                and sanitized patterns that compound across locations; dual-write
                IP ownership (memory may also live in Anthropic-side caches for
                performance, but the canonical copy is always in our Supabase);
                hybrid Coordinator (cheap rules-based Stage 1 catches ~65%,
                reasoning-agent Stage 2 reserved for ambiguous cases in a later
                phase); PR-style supervision on every Build change.
              </p>
              <p>
                <strong>Schema in production</strong> at{' '}
                <code>dyoqjqhkfxpabgzbtetw</code> Supabase: a dedicated{' '}
                <code>agent_memory</code> schema isolated from <code>public</code>{' '}
                holds seven tables (<code>agents</code>, <code>patterns</code>,{' '}
                <code>embeddings</code>, <code>sessions</code>,{' '}
                <code>artifacts</code>, <code>events</code>,{' '}
                <code>sanitizer_queue</code>). <code>pgvector</code> 1536-dim
                embeddings sized for <code>text-embedding-3-small</code> with an
                HNSW cosine-similarity index. Sessions default to a 7-day
                <code>expires_at</code>. RLS enabled on every table; service-role
                bypasses; the seeded agent registry holds 14 rows (12 specialists
                + Coordinator + Sanitizer).
              </p>
              <p>
                <strong>The PostgREST exposure gap and the RPC-wrapper resolution
                </strong>: first prod smoke of the Sanitizer returned <code>PGRST106
                · Only the following schemas are exposed: public, graphql_public</code>{' '}
                because the project's <code>db_schemas</code> config never added{' '}
                <code>agent_memory</code> to its REST surface. Two architecturally
                clean options: open the schema for REST access (lowest code churn,
                broader attack surface), or write SECURITY DEFINER RPCs in{' '}
                <code>public</code> that internally touch{' '}
                <code>agent_memory</code> (kept unexposed, tighter audit story).
                Logged as <strong>Q-002</strong> in{' '}
                <code>COORDINATOR_QUEUE.md</code>; resolution chose the RPC path
                so <code>agent_memory</code> stays platform-internal forever. Five
                wrappers shipped across three migrations:{' '}
                <code>sanitizer_lookup_agent</code>,{' '}
                <code>sanitizer_record_event</code>,{' '}
                <code>sanitizer_enqueue_review</code> (migration v1.1 for the
                Sanitizer trio), <code>agent_record_event</code> (v1.2, the
                generalized audit-write the entire platform uses), and{' '}
                <code>agent_session_create</code> (v1.3, the Coordinator's
                session-write path). All five are <code>SECURITY DEFINER</code>{' '}
                with <code>set search_path = ''</code> to defend against
                search-path injection, and a <code>revoke execute from anon,
                authenticated</code> baked into the same SQL file as the function
                definition (after the first SECURITY DEFINER landed without the
                explicit revoke — Supabase pre-grants execute to those roles —
                and required a lockdown follow-up migration; captured in the
                Engineer Journal so future RPCs ship with the revoke built in).
              </p>
              <p>
                <strong>The Sanitizer pipeline</strong>:{' '}
                <code>supabase/functions/sanitize/index.ts</code> calls a
                heuristic Stage 1 (regex strip for emails, phones, dollars,
                dates, known location names, API keys / JWT-shaped tokens) and,
                if confidence stays below threshold or any uncertain capitalized
                spans remain, escalates to a Stage 2 Anthropic Claude Haiku call
                with a deterministic prompt asking explicitly whether the
                remaining text could re-identify a specific customer. Stage 2
                verdict <code>safe</code> auto-approves; <code>needs_review</code>{' '}
                inserts a row into <code>agent_memory.sanitizer_queue</code>{' '}
                with <code>status='pending'</code>. Every call writes an audit
                event regardless of outcome. The function refused four smoke
                inputs correctly on first deploy: a safe pattern passed through
                with zero redactions, an email got{' '}
                <code>[EMAIL]</code>-stripped, a borderline "28-suite studio
                in central MA" got queued for review with a Stage 2 explanation
                pointing at the suite count plus geographic locator as the
                re-identification risk, and an API-key-shaped string got{' '}
                <code>[REDACTED]</code> at Stage 1 before Stage 2 even ran.
              </p>
              <p>
                <strong>The framework that ran the build itself</strong> ·
                multi-agent coordination via git: every work item lives in{' '}
                <code>Work Items/WI-NNN_*.md</code> with a strict lifecycle
                (OPEN → IN_PROGRESS → DONE / BLOCKED / REJECTED), every
                cross-WI architectural question is logged as a{' '}
                <code>Q-NNN</code> entry in{' '}
                <code>COORDINATOR_QUEUE.md</code> with a structured
                answer block, every engineer-discovered gotcha gets appended
                to <code>ENGINEER_JOURNAL.md</code> so the next engineer
                doesn't re-discover it the hard way. Feature branches per WI in{' '}
                <code>pknt-agents</code> (<code>wi-NNN/&lt;short-slug&gt;</code>{' '}
                merged to <code>main</code> with{' '}
                <code>--no-ff</code> only at WI completion) became mandatory
                after one engineer's <code>git add -A</code> accidentally
                staged another's WIP files mid-session; the structural fix
                eliminated the cross-WIP collision class entirely. Nine work
                items shipped this arc: WI-000 (migrate knowledge base from
                Google Drive to local + git), WI-001 (migrate three more
                Drive-hosted repos to local), WI-002 (initialize the{' '}
                <code>pknt-agents</code> repo with bootstrap templates), WI-003
                (apply the <code>agent_memory</code> v1 schema), WI-004 (build
                the Sanitizer Edge Function), WI-005 (audit-logging client
                shared module), WI-006 (RLS-scoped tool inventory pattern with
                three initial read tools and a critical cross-location isolation
                test), WI-007 (Coordinator Stage 1 fast classifier), WI-009
                (refactor the audit client to the RPC pattern after Q-002 was
                resolved · WI-005 had landed with the pre-Q-002 shape).
                WI-008 (Mac mini self-hosted GitHub Actions runner for iOS
                tests) ran in parallel against{' '}
                <code>pknt-ops-ios</code> and is still in flight.
              </p>
              <p>
                <strong>Production endpoints</strong>:{' '}
                <code>https://dyoqjqhkfxpabgzbtetw.supabase.co/functions/v1/sanitize</code>{' '}
                (Sanitizer · <code>verify_jwt: true</code> · service-role bearer)
                and{' '}
                <code>https://dyoqjqhkfxpabgzbtetw.supabase.co/functions/v1/coordinator</code>{' '}
                (Coordinator dispatch). Both deployed via Supabase Functions and
                running against the live agent_memory schema. 143 automated
                tests across <code>pknt-agents</code> at last green check
                (116 from earlier WIs + 17 Coordinator classifier rule tests + 10
                end-to-end dispatch tests). Tests cover happy-path classification,
                ambiguous-input escalation, missing-input rejection, oversize-input
                rejection, mock-supabase RPC contracts, and cross-location data
                leak prevention through the tool layer.
              </p>
              <p>
                <strong>What this is the foundation for</strong>: Phase 2 of the
                Managed Agents arc — wiring an actual iOS Support agent (real
                Claude prompt, real tool access, real memory retrieval via vector
                similarity) end-to-end through the Coordinator into the live
                stack. The infrastructure that just landed is the runway every
                subsequent agent taxis on. The platform stops being "a list of
                features Peter manually operates" and starts being "a thing that
                operates itself, sharpens itself, and scales by adding sibling
                workers when load demands."
              </p>
            </div>
          </details>
        </Phase>

        {/* ─── System cards ─────────────────────────────── */}
        <h2 className="font-display text-3xl text-brand-deep mt-24 mb-2">
          What's running in production
        </h2>
        <p className="text-sm text-brand-slate mb-8">
          Six distinct systems, all sharing one Postgres database. Same auth. Same row-level
          security. Same business rules.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-16">
          <SystemCard
            n="01"
            title="iOS PKNTOps"
            tag="Operator app"
            stats={[
              { label: 'commits', value: '429' },
              { label: 'lines of Swift', value: '72,000' },
              { label: 'active days', value: '18' },
            ]}
          >
            Native Swift + SwiftUI app for the operator running the location. Five tabs: Home,
            Dash, Suites, Ops, Agent. Floor plan with color-coded occupancy. Voice-driven AI
            assistant with 33 tools and confirmation-gated writes. Runs on iPhone, iPad, and Apple
            Vision Pro with adaptive layouts.
          </SystemCard>

          <SystemCard
            n="02"
            title="IMAGE Pro Hub"
            tag="Pro-facing web"
            stats={[
              { label: 'commits', value: '62' },
              { label: 'lines of TS', value: '8,500' },
              { label: 'active days', value: '9' },
            ]}
          >
            React + Vite + Tailwind web app at pkntops.com. Magic-link sign-in. Suites, leases,
            tickets, COI uploads, referrals, spotlights, events. Same Supabase as iOS — every Pro
            sees the same data their operator sees, scoped by row-level security.
          </SystemCard>

          <SystemCard
            n="03"
            title="Supabase backend"
            tag="The platform"
            stats={[
              { label: 'commits', value: '49' },
              { label: 'SQL migrations', value: '19' },
              { label: 'Edge Functions', value: '~30' },
            ]}
          >
            Postgres + Edge Functions + pg_cron + RLS. Single source of truth. Tags are columns,
            workflows are scheduled rows on a queue, and a worker drains the queue every minute.
            All auditable, all version-controlled, all reproducible per-location with a config row.
          </SystemCard>

          <SystemCard
            n="04"
            title="Workflow engine"
            tag="Built where there used to be GHL workflows"
            stats={[
              { label: 'workflows migrated', value: '12 / 34' },
              { label: 'message templates', value: '19' },
              { label: 'goal exit tags', value: '4' },
            ]}
          >
            <code>scheduled_messages</code> queue · <code>workflow-dispatcher</code> Edge Function
            · <code>workflow_audit_log</code> append-only history · <code>ghl_event_bindings</code>{' '}
            lookup table. Engagement-respecting at the row level: every send checks the contact's
            current state before firing.
          </SystemCard>

          <SystemCard
            n="05"
            title="Voice AI + Live Channels"
            tag="GHL stays, but as a pipe"
            stats={[
              { label: 'GHL workflows', value: '"Doorbell only"' },
              { label: 'channels', value: 'SMS · Voice · Social DM' },
              { label: 'A2P status', value: 'Live' },
            ]}
          >
            GoHighLevel keeps what it's good at: the Twilio DID, A2P 10DLC SMS, conversation
            threads, calendar bookings, social inbox. The brain moves to Supabase. GHL becomes a
            tag-add doorbell → webhook pipe.
          </SystemCard>

          <SystemCard
            n="06"
            title="Quality &amp; Ops Portal"
            tag="DD-readiness asset"
            stats={[
              { label: 'test cases', value: '223' },
              { label: 'DD artifacts indexed', value: '34' },
              { label: 'deployed at', value: 'pkntops.com/admin/qa' },
            ]}
          >
            A live test management + defect tracking + audit artifact index, designed as an
            IMAGE-Corporate-readable due-diligence asset. corporate_admin gated. All data live in
            Supabase; dashboards composed in real time.
          </SystemCard>
        </div>

        {/* ─── Pivots ──────────────────────────────────── */}
        <h2 className="font-display text-3xl text-brand-deep mt-24 mb-2">
          Three pivots in five weeks
        </h2>
        <p className="text-sm text-brand-slate mb-8">
          Each one stripped a layer from the architecture. Each one moved closer to{' '}
          <em>"replicate to a new location with a config row, not a copy-paste."</em>
        </p>

        <Pivot date="April 20" title="CloudKit retired as durability">
          The iOS app launched four weeks earlier with CloudKit + iCloud as load-bearing
          infrastructure — every device was supposed to be a peer. The pivot:{' '}
          <strong>iOS becomes a client</strong>, BASE44 becomes the canonical backend, SwiftData
          becomes a write-through cache. Two paragraphs in a single document closed the loop.
        </Pivot>

        <Pivot date="April 23" title="BASE44 retired as backend">
          The BASE44 platform was capped at $80/month and 500 monthly AI credits. The pivot:{' '}
          <strong>Supabase becomes the platform.</strong> Real Postgres, real RLS, real
          migrations, $25/month base cost. Killing BASE44 saves ~$4k/mo at national scale.
        </Pivot>

        <Pivot date="April 25" title="GHL retired as workflow engine (in progress)">
          The 34 GHL workflows that drove every drip, reminder, and follow-up were the
          franchise-replication blocker. The pivot, captured in ADR-023:{' '}
          <strong>GHL becomes a dumb pipe.</strong> Tags become columns. Workflows become Edge
          Functions and pg_cron jobs. 12 of 34 already migrated as of May 6.
        </Pivot>

        {/* ─── Quotable lines ──────────────────────────── */}
        <h2 className="font-display text-3xl text-brand-deep mt-24 mb-8">
          Lines that stuck
        </h2>

        <div className="space-y-6">
          <Quote source="BACKEND_MIGRATION_PLAN.md, April 20">
            SwiftData becomes a pure offline cache, CloudKit stops being load-bearing, and every
            write and read in the iOS app goes through Base44.
          </Quote>
          <Quote source="ADR-023, April 25">
            Provisioning a new owner today requires recreating the workflow graph by hand.
            Provisioning a new owner under this ADR requires three SQL inserts. Replication shifts
            from "weeks of forensic recreation" to "30 minutes of provisioning."
          </Quote>
          <Quote source="national_platform_plan.md, April 24">
            One Supabase project. <code>location_id</code> on every row. RLS everywhere.
          </Quote>
          <Quote source="RECOVERY_NOTES_2026-04-22.md, April 22">
            Big day. Three narrative arcs: cleaning log MVP shipped, automated testing stack built
            from scratch, infra cleanup + consolidated key management.
          </Quote>
        </div>

        {/* ─── Closing ────────────────────────────────── */}
        <h2 className="font-display text-3xl text-brand-deep mt-24 mb-8">Where this lands</h2>

        <p>
          A salon-suite operator in Sharon, Massachusetts spent five weeks rebuilding his ops
          platform from a stack of disconnected SaaS tools into a single Postgres-anchored
          platform that can replicate to every IMAGE Studios franchise location with a config
          change.
        </p>
        <p>
          Three migrations. Five distinct phases. <strong>540 commits, 89,000 lines of code,
          129,000 lines of churn on iOS alone, 19 SQL migrations, 182 architecture documents.</strong>{' '}
          Two production bugs (one of which — a real prospect getting a cold-cadence SMS one day
          after booking her tour — directly motivated the GHL workflow migration that's now
          half-shipped). One App Store submission, one Apple Review demo account, and one
          operational architecture that didn't exist on March 28 and now runs three locations.
        </p>
        <p>
          The infrastructure cost at 150 locations: <strong>~$100–$200/month total.</strong>{' '}
          Supabase Pro $25, Fly.io n8n $20, Apple Developer $8, Cloudflare Pages $0, GHL already
          paid. Killing BASE44 saves $4,000+/month at national scale. Adding location 151 costs
          one row in <code>locations</code> and one row in <code>location_configs</code>.
        </p>
        <p>
          The rebuild ships every day. The architecture is the IMAGE Corporate pitch.
        </p>

        <hr className="my-16 border-brand-mist" />

        <p className="text-xs text-brand-slate text-center leading-relaxed">
          Built by Peter Lewis · PKNT Industries Inc · 15 Cattail Lane, Sharon MA 02067
          <br />
          Source data: 182 markdown files in <code>_backups/MD Files</code>; git history of three
          repositories on <code>github.com/plewis-pknt</code>; Supabase audit logs.
          <br />
          Generated 2026-05-06. Updated 2026-05-07 with the readiness phase narrative.
        </p>
      </main>

      {/* ─── Footer ───────────────────────────────────── */}
      <footer className="px-6 py-8 max-w-3xl mx-auto text-center border-t border-brand-mist">
        <a href="/" className="text-sm text-brand-slate hover:text-image-pink">
          ← Back to PKNT Ops
        </a>
      </footer>
    </div>
  );
}

/* ────────────────────────────────────────────────── */
/*  Sub-components                                    */
/* ────────────────────────────────────────────────── */

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <div className="bg-brand-fog p-6 text-center">
      <div className="font-display text-4xl sm:text-5xl text-image-pink leading-none mb-2">
        {n}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-brand-slate leading-snug">
        {label}
      </div>
    </div>
  );
}

function Phase({
  number,
  title,
  subtitle,
  tag,
  children,
}: {
  number: string;
  title: string;
  subtitle: string;
  tag: string;
  children: React.ReactNode;
}) {
  return (
    <article className="mb-20">
      <div className="flex items-baseline gap-4 mb-4">
        <div className="font-display text-5xl text-image-pink/30 leading-none">{number}</div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.2em] text-image-pink font-semibold mb-1">
            {tag}
          </p>
          <h3 className="font-display text-2xl text-brand-deep leading-tight">{title}</h3>
          <p className="text-sm text-brand-slate mt-1">{subtitle}</p>
        </div>
      </div>
      <div className="prose prose-sm sm:prose-base max-w-none prose-p:text-brand-slate prose-p:leading-relaxed prose-strong:text-brand-deep prose-code:text-image-pink prose-code:bg-image-pink-soft/30 prose-code:px-1 prose-code:rounded prose-code:before:hidden prose-code:after:hidden">
        {children}
      </div>
    </article>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-l-2 border-image-pink pl-6 my-8 italic text-lg text-brand-deep leading-relaxed">
      {children}
    </div>
  );
}

function SystemCard({
  n,
  title,
  tag,
  stats,
  children,
}: {
  n: string;
  title: string;
  tag: string;
  stats: { label: string; value: string }[];
  children: React.ReactNode;
}) {
  return (
    <div className="border border-brand-mist p-6 bg-white/40">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-image-pink font-semibold mb-1">
            {tag}
          </p>
          <h4 className="font-display text-lg text-brand-deep leading-tight">{title}</h4>
        </div>
        <div className="font-display text-2xl text-image-pink/30">{n}</div>
      </div>
      <p className="text-sm text-brand-slate leading-relaxed mb-4">{children}</p>
      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-brand-mist">
        {stats.map((s) => (
          <div key={s.label}>
            <div className="text-xs font-semibold text-brand-deep">{s.value}</div>
            <div className="text-[9px] uppercase tracking-wider text-brand-slate">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Pivot({
  date,
  title,
  children,
}: {
  date: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-8 grid grid-cols-[80px_1fr] gap-6 items-baseline">
      <div className="text-xs uppercase tracking-wider text-image-pink font-semibold">{date}</div>
      <div>
        <h4 className="font-display text-xl text-brand-deep mb-2">{title}</h4>
        <p className="text-sm text-brand-slate leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

function Quote({ source, children }: { source: string; children: React.ReactNode }) {
  return (
    <blockquote className="border-l-2 border-brand-mist pl-6 py-2">
      <p className="text-base text-brand-deep leading-relaxed mb-2">"{children}"</p>
      <footer className="text-[11px] uppercase tracking-wider text-brand-slate">{source}</footer>
    </blockquote>
  );
}
